import { useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { CheckCircle2, FileText, Link2 } from 'lucide-react'
import { useFormStore } from '../store/formStore'
import { delay } from '../mock/mockDelay'
import { submitApplication } from '../api/applicant'
import { ApiError } from '../api/client'
import { useSession } from '../apply/session'
import { PageShell } from '../components/layout/PageShell'
import { Button } from '../components/ui/Button'
import { BackButton } from '../components/ui/BackButton'
import { Card } from '../components/ui/Card'
import { SaveIndicator } from '../components/ui/SaveIndicator'
import { Checkbox } from '../components/ui/Checkbox'
import { RowTile } from '../components/ui/RowTile'
import { MetaValue } from '../components/ui/MetaValue'
import { EntryCard } from '../components/ui/EntryCard'
import { REGIONS } from '../constants/processingBreakdown'
import { DOCUMENT_SLOT_LABELS } from '../constants/documentSlots'

// ---------------------------------------------------------------------------
// Animation variants
// ---------------------------------------------------------------------------
const cardVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.2, duration: 0.4, ease: 'easeOut' as const },
  }),
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------
interface ReviewFieldProps {
  label: string
  value: string
  /** Optional leading glyph in the value row (e.g. the "Linked to" link icon). */
  icon?: ReactNode
}

function ReviewField({ label, value, icon }: ReviewFieldProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
      <span
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-label-sm)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-secondary)',
          textTransform: 'uppercase',
          letterSpacing: '0.03em',
        }}
      >
        {label}
      </span>
      <span
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          fontFamily: 'var(--font-body)',
          fontSize: 'var(--text-body-md)',
          color: 'var(--color-text-primary)',
          lineHeight: 'var(--leading-normal)',
        }}
      >
        {icon}
        {value || '—'}
      </span>
    </div>
  )
}

interface CardHeaderProps {
  title: string
  onEdit: () => void
}

function CardHeader({ title, onEdit }: CardHeaderProps) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 'var(--spacing-8)',
      }}
    >
      <h3
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title-md)',
          fontWeight: 'var(--weight-semibold)',
          color: 'var(--color-text-primary)',
          margin: 0,
        }}
      >
        {title}
      </h3>
      <Button variant="tertiary" onClick={onEdit} style={{ fontSize: 'var(--text-body-sm)' }}>
        Edit
      </Button>
    </div>
  )
}

// ---------------------------------------------------------------------------
// ReviewScreen
// ---------------------------------------------------------------------------
export function ReviewScreen() {
  const navigate = useNavigate()
  const store = useFormStore()
  const { applyApplication, documents } = useSession()

  const [confirmedAccurate, setConfirmedAccurate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showConfirmWarning, setShowConfirmWarning] = useState(false)
  const [submitError, setSubmitError] = useState(false)

  const allRequiredConfirmed = confirmedAccurate

  // All values come from the server-persisted formData; empty renders as an
  // em-dash via ReviewField.
  const fd = store.formData as Record<string, string>

  const companyName = (fd.legalCompanyName as string) || ''
  const regNumber = (fd.registrationNumber as string) || ''
  const incDate = (fd.incorporationDate as string) || ''
  const countryOfInc = (fd.countryOfIncorporation as string) || ''
  // Registered office address — prefer the single field (P4), fall back to
  // the 3-part split from the Company Structure screen.
  const legacyAddress = [
    fd.registeredAddressStreet,
    fd.registeredAddressCity,
    fd.registeredAddressCountry,
  ]
    .filter(Boolean)
    .join(', ')
  const regAddress = (fd.registeredOfficeAddress as string) || legacyAddress

  // Owners / shareholders — persisted by CompanyStructureScreen as plain
  // arrays (person: firstName/lastName; holding: legalCompanyName).
  interface StoredOwner {
    kind?: 'person' | 'holding'
    firstName?: string
    lastName?: string
    legalCompanyName?: string
    ownershipPct?: number
    nationality?: string
    dob?: string
    passportNumber?: string
    placeOfIssue?: string
    registeredAddress?: string
    countryOfIncorporation?: string
    registrationNumber?: string
  }
  interface StoredDirector {
    // Legacy shape (pre firstName/lastName split); still present in
    // already-submitted applications saved before the split.
    name?: string
    firstName?: string
    lastName?: string
    nationality?: string
    dob?: string
    passportNumber?: string
    placeOfIssue?: string
    isAuthorisedSignatory?: boolean
    linkedOwnerName?: string
  }
  const rawFd = store.formData as Record<string, unknown>
  const owners = Array.isArray(rawFd.owners) ? (rawFd.owners as StoredOwner[]) : []
  const directors = Array.isArray(rawFd.directors) ? (rawFd.directors as StoredDirector[]) : []

  function ownerDisplayName(o: StoredOwner): string {
    if (o.kind === 'holding') return o.legalCompanyName || '—'
    return `${o.firstName ?? ''} ${o.lastName ?? ''}`.trim() || '—'
  }

  function directorDisplayName(d: StoredDirector): string {
    const split = `${d.firstName ?? ''} ${d.lastName ?? ''}`.trim()
    return split || d.name || '—'
  }

  // Total ownership %
  const totalOwnership = owners.reduce((sum, o) => sum + (o.ownershipPct || 0), 0)

  // MAF 26-05: Processing Breakdown summary (read-only). Only regions with a
  // value > 0 are listed; all-zero is the empty-state demo.
  const regionBreakdown = REGIONS.map((r) => ({ label: r.label, value: Number(fd[r.key]) || 0 })).filter(
    (e) => e.value > 0
  )
  const monthlyVolume = Number(fd.estimated_monthly_volume) || 0

  // Company License: the applicant either holds one (fields captured) or
  // declared none. Mirrors the read-only detail view's Company License card.
  const noLicenceHeld = rawFd.noLicence as boolean | undefined

  async function handleSubmit() {
    if (!allRequiredConfirmed) {
      setShowConfirmWarning(true)
      setTimeout(() => setShowConfirmWarning(false), 3500)
      return
    }
    setSubmitting(true)
    try {
      // Keep the submit animation on screen for a moment even if the API
      // responds instantly.
      const [{ application }] = await Promise.all([submitApplication(), delay(2500)])
      applyApplication(application)
      store.setStep(4)
      store.markSubmitted(application.submitted_at ?? undefined)
      navigate('/success')
    } catch (err) {
      if (err instanceof ApiError && err.code === 'application.already_submitted') {
        navigate('/history')
        return
      }
      setSubmitting(false)
      setShowConfirmWarning(false)
      setSubmitError(true)
      setTimeout(() => setSubmitError(false), 4000)
    }
  }

  return (
    <PageShell showStepper={true} currentStep={4}>
      {/* Override inner max-width to 800px for review */}
      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        {/* Page heading row */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-2)',
          }}
        >
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-title-lg)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--color-text-primary)',
              margin: 0,
            }}
          >
            Review your application
          </h1>
          <SaveIndicator state="saved" />
        </div>

        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-body-md)',
            color: 'var(--color-text-secondary)',
            marginTop: 0,
            marginBottom: 'var(--spacing-8)',
          }}
        >
          Please review all the information below before submitting. You can edit any section by clicking the Edit button.
        </p>

        {/* ----------------------------------------------------------------
            Card 0: Your Contact Details
        ---------------------------------------------------------------- */}
        <motion.div
          custom={0}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-6)' }}
        >
          <Card padding="lg">
            <CardHeader title="Your Contact Details" onEdit={() => navigate('/welcome')} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <ReviewField
                label="First name"
                value={(fd.firstName as string) || ''}
              />
              <ReviewField
                label="Last name"
                value={(fd.lastName as string) || ''}
              />
              <ReviewField
                label="Job title"
                value={(fd.jobTitle as string) || ''}
              />
              <ReviewField
                label="Company email address"
                value={(fd.authEmail as string) || ''}
              />
              <ReviewField
                label="Company website"
                value={(fd.companyWebsite as string) || ''}
              />
            </div>
          </Card>
        </motion.div>

        {/* ----------------------------------------------------------------
            Card 1: Company Details
        ---------------------------------------------------------------- */}
        <motion.div
          custom={1}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-6)' }}
        >
          <Card padding="lg">
            <CardHeader title="Company Details" onEdit={() => navigate('/company-details')} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
              <ReviewField label="Legal company name" value={companyName} />
              <ReviewField label="Company registration number" value={regNumber} />
              <ReviewField label="Date of incorporation" value={incDate} />
              <ReviewField label="Country of incorporation" value={countryOfInc} />
              <ReviewField label="Registered office country" value={(fd.registeredAddressCountry as string) || ''} />
              <ReviewField label="Registered office address" value={regAddress} />
            </div>
          </Card>
        </motion.div>

        {/* ----------------------------------------------------------------
            Card 1b: Company License
        ---------------------------------------------------------------- */}
        <motion.div
          custom={2}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-6)' }}
        >
          <Card padding="lg">
            <CardHeader title="Company License" onEdit={() => navigate('/company-details')} />
            {noLicenceHeld ? (
              <span style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--text-body-md)', color: 'var(--color-text-secondary)' }}>
                No licence has been provided.
              </span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}>
                <ReviewField label="License name" value={fd.licenseType || ''} />
                <ReviewField label="License number" value={fd.licenseNumber || ''} />
                {/* Legacy fields, removed from the form 22-07 — shown only when an
                    older application recorded them. */}
                {Boolean(fd.licenseRegulator) && (
                  <ReviewField label="Name of regulator" value={fd.licenseRegulator || ''} />
                )}
                {Boolean(fd.licenseExpiryDate) && (
                  <ReviewField label="License expiration date" value={fd.licenseExpiryDate || ''} />
                )}
              </div>
            )}
          </Card>
        </motion.div>

        {/* ----------------------------------------------------------------
            Card 2: Company Structure
        ---------------------------------------------------------------- */}
        <motion.div
          custom={3}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-6)' }}
        >
          <Card padding="lg">
            <CardHeader title="Company Ownership" onEdit={() => navigate('/company-structure')} />

            {/* Beneficial Owners */}
            <div style={{ marginBottom: 'var(--spacing-6)' }}>
              <h4
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-label-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  margin: '0 0 var(--spacing-3) 0',
                }}
              >
                Beneficial Owners
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                {owners.map((owner, idx) => {
                  const isHolding = owner.kind === 'holding'
                  return (
                    <EntryCard
                      key={idx}
                      title={ownerDisplayName(owner)}
                      meta={`${owner.ownershipPct ?? 0}%`}
                    >
                      {isHolding ? (
                        <>
                          <ReviewField label="Type" value="Holding company" />
                          <ReviewField label="Country of incorporation" value={owner.countryOfIncorporation || ''} />
                          <ReviewField label="Registration number" value={owner.registrationNumber || ''} />
                        </>
                      ) : (
                        <>
                          <ReviewField label="Type" value="Individual" />
                          <ReviewField label="Date of birth" value={owner.dob || ''} />
                          <ReviewField label="Nationality" value={owner.nationality || ''} />
                          <ReviewField label="Passport number" value={owner.passportNumber || ''} />
                          <ReviewField label="Place of issue" value={owner.placeOfIssue || ''} />
                          <ReviewField label="Registered address" value={owner.registeredAddress || ''} />
                        </>
                      )}
                    </EntryCard>
                  )
                })}
              </div>
              {/* Total ownership */}
              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 'var(--spacing-2)',
                }}
              >
                <span
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-label-sm)',
                    fontWeight: 'var(--weight-semibold)',
                    color: totalOwnership === 100
                      ? 'var(--color-success-text)'
                      : 'var(--color-warning-text)',
                  }}
                >
                  Total ownership: {totalOwnership}%
                  {totalOwnership === 100 && ' ✓'}
                </span>
              </div>
            </div>

            {/* Directors */}
            <div>
              <h4
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-label-sm)',
                  fontWeight: 'var(--weight-semibold)',
                  color: 'var(--color-text-secondary)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.03em',
                  margin: '0 0 var(--spacing-3) 0',
                }}
              >
                Directors
              </h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                {directors.map((director, idx) => (
                  <EntryCard
                    key={idx}
                    title={directorDisplayName(director)}
                    meta={director.isAuthorisedSignatory ? 'Authorised signatory' : undefined}
                  >
                    <ReviewField label="Date of birth" value={director.dob || ''} />
                    <ReviewField label="Nationality" value={director.nationality || ''} />
                    <ReviewField label="Passport number" value={director.passportNumber || ''} />
                    <ReviewField label="Place of issue" value={director.placeOfIssue || ''} />
                    {director.linkedOwnerName && (
                      <ReviewField
                        label="Linked to"
                        value={director.linkedOwnerName}
                        icon={<Link2 size={15} aria-hidden="true" style={{ color: 'var(--color-action-primary)', flexShrink: 0 }} />}
                      />
                    )}
                  </EntryCard>
                ))}
              </div>
            </div>
          </Card>
        </motion.div>

        {/* ----------------------------------------------------------------
            Card 2b: Processing Breakdown (MAF 26-05) — kept directly above
            Uploaded Documents, matching the read-only application detail view.
        ---------------------------------------------------------------- */}
        <motion.div
          custom={4}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-6)' }}
        >
          <Card padding="lg">
            <CardHeader title="Processing Breakdown" onEdit={() => navigate('/processing-breakdown')} />
            {regionBreakdown.length === 0 && monthlyVolume === 0 ? (
              <span
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--text-body-md)',
                  color: 'var(--color-text-tertiary)',
                }}
              >
                Not provided
              </span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                {regionBreakdown.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
                    {regionBreakdown.map((e) => (
                      <RowTile key={e.label} style={{ justifyContent: 'space-between' }}>
                        <span
                          style={{
                            fontFamily: 'var(--font-body)',
                            fontSize: 'var(--text-body-md)',
                            color: 'var(--color-text-primary)',
                          }}
                        >
                          {e.label}
                        </span>
                        <MetaValue>{e.value}%</MetaValue>
                      </RowTile>
                    ))}
                  </div>
                )}
                <ReviewField
                  label="Estimated monthly volume (USD)"
                  value={monthlyVolume > 0 ? `$${monthlyVolume.toLocaleString('en-US')}` : ''}
                />
              </div>
            )}
          </Card>
        </motion.div>

        {/* ----------------------------------------------------------------
            Card 3: Documents
        ---------------------------------------------------------------- */}
        <motion.div
          custom={5}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-8)' }}
        >
          <Card padding="lg">
            <CardHeader title="Uploaded Documents" onEdit={() => navigate('/company-details')} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
              {documents.length === 0 ? (
                <span
                  style={{
                    fontFamily: 'var(--font-body)',
                    fontSize: 'var(--text-body-md)',
                    color: 'var(--color-text-tertiary)',
                  }}
                >
                  Not provided
                </span>
              ) : (
                documents.map((doc) => {
                  const label = DOCUMENT_SLOT_LABELS[doc.slot as keyof typeof DOCUMENT_SLOT_LABELS] ?? doc.slot
                  return (
                    <RowTile key={doc.id} style={{ gap: 'var(--spacing-3)' }}>
                      <FileText
                        size={18}
                        style={{ color: 'var(--color-action-primary)', flexShrink: 0 }}
                        aria-hidden="true"
                      />
                      <span
                        style={{
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-body-md)',
                          color: 'var(--color-text-primary)',
                          flex: 1,
                        }}
                      >
                        {label}
                        {doc.slot_index != null ? ` #${doc.slot_index + 1}` : ''}
                      </span>
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--spacing-1)',
                          fontFamily: 'var(--font-body)',
                          fontSize: 'var(--text-label-sm)',
                          fontWeight: 'var(--weight-semibold)',
                          color: 'var(--color-success-text)',
                        }}
                      >
                        <CheckCircle2 size={14} aria-hidden="true" />
                        Uploaded
                      </span>
                    </RowTile>
                  )
                })
              )}
            </div>
          </Card>
        </motion.div>

        {/* ----------------------------------------------------------------
            Confirmation checkboxes (spec requires 3)
        ---------------------------------------------------------------- */}
        <motion.div
          custom={6}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-6)', display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)' }}
        >
          {/* Checkbox 1 — required */}
          <label
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              alignItems: 'flex-start',
              cursor: 'pointer',
            }}
          >
            <Checkbox
              checked={confirmedAccurate}
              onChange={(checked) => {
                setConfirmedAccurate(checked)
                if (checked) setShowConfirmWarning(false)
              }}
              style={{ marginTop: 2 }}
            />
            <span
              style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--text-body-md)',
                color: 'var(--color-text-primary)',
                lineHeight: 'var(--leading-normal)',
              }}
            >
              I confirm that all information provided is accurate, complete, and up to date to the best of my knowledge.
            </span>
          </label>

        </motion.div>

        {/* Confirmation warning */}
        {showConfirmWarning && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              marginBottom: 'var(--spacing-4)',
              padding: '10px 16px',
              background: 'var(--color-warning-surface)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-warning-text)',
            }}
          >
            Please confirm the information above before submitting.
          </motion.div>
        )}

        {/* Submission failure */}
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            style={{
              marginBottom: 'var(--spacing-4)',
              padding: '10px 16px',
              background: 'var(--color-error-surface)',
              borderRadius: 'var(--radius-md)',
              fontFamily: 'var(--font-body)',
              fontSize: 'var(--text-body-sm)',
              color: 'var(--color-error-text)',
            }}
          >
            Something went wrong while submitting. Your answers are saved — please try again.
          </motion.div>
        )}

        {/* ----------------------------------------------------------------
            Submit button
        ---------------------------------------------------------------- */}
        <motion.div
          custom={7}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-4)' }}
        >
          <div style={{ display: 'flex', marginBottom: 'var(--spacing-4)' }}>
            <BackButton to="/processing-breakdown" />
          </div>
          <motion.div
            whileHover={allRequiredConfirmed && !submitting ? { scale: 1.015 } : {}}
            whileTap={allRequiredConfirmed && !submitting ? { scale: 0.98 } : {}}
            style={{ borderRadius: 'var(--radius-xl)' }}
          >
            <Button
              variant="primary"
              loading={submitting}
              onClick={handleSubmit}
              disabled={!allRequiredConfirmed}
              style={{ width: '100%' }}
            >
              {submitting ? 'Submitting...' : 'Submit Application'}
            </Button>
          </motion.div>
        </motion.div>

        {/* Data deletion link — sits under the Submit button, centered */}
        <motion.div
          custom={8}
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          style={{ marginBottom: 'var(--spacing-10)' }}
        >
          <p style={{ margin: 0, textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 'var(--text-label-sm)' }}>
            <a
              href="https://zota.com/data-deletion"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--color-action-primary)', textDecoration: 'underline' }}
            >
              Learn about your right to request data deletion.
            </a>
          </p>
        </motion.div>
      </div>
    </PageShell>
  )
}
